/**
 * PURPOSE: Barra de scroll horizontal flotante, fija al borde inferior de la ventana, que replica el scroll
 *   horizontal de un contenedor ancho cuya barra nativa queda al final de un listado largo.
 * RESPONSIBILITIES: Mostrarse solo si el contenedor tiene overflow horizontal (scrollWidth > clientWidth), está a
 *   la vista y su barra nativa quedó por debajo del borde inferior de la ventana; alinearse con el ancho visible
 *   del contenedor sin ocupar espacio en el layout (position: fixed en un portal); sincronizar scrollLeft en
 *   ambos sentidos ignorando el eco de sus propias asignaciones. Con barras superpuestas (overlay: móvil, macOS
 *   con trackpad) no se muestra: el contenedor ya se desplaza con el gesto y la barra propia quedaría vacía.
 *   Es un espejo para puntero: el contenedor real sigue siendo el que usan el teclado y los lectores de pantalla.
 * COLLABORATORS: CompanyUniformsTab.jsx (matriz "Opciones por equipo").
 * ROUTE: winscore/src/components/FloatingHorizontalScrollbar/FloatingHorizontalScrollbar.jsx
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Alto mínimo del contenedor que debe verse por encima de la barra para que tenga sentido mostrarla.
const MIN_VISIBLE_PX = 48;

const sameMetrics = (a, b) =>
  a === b || Boolean(a && b && a.left === b.left && a.width === b.width && a.scrollWidth === b.scrollWidth);

/** Geometría de la barra flotante para `target`, o null cuando no debe mostrarse. */
const measure = (target) => {
  if (!target || target.scrollWidth <= target.clientWidth) return null;
  const style = window.getComputedStyle(target);
  const borderY = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  if (target.offsetHeight - target.clientHeight - borderY <= 0) return null;
  const rect = target.getBoundingClientRect();
  if (rect.bottom <= window.innerHeight || rect.top > window.innerHeight - MIN_VISIBLE_PX) return null;
  return { left: rect.left + target.clientLeft, width: target.clientWidth, scrollWidth: target.scrollWidth };
};

/**
 * Copia scrollLeft de `from` a `to`. `echo` guarda el valor que asignamos a cada lado: el evento scroll que esa
 * asignación dispara llega en el frame siguiente y, si se reenviara, tiraría hacia atrás de la barra que el
 * usuario sigue arrastrando.
 */
const syncScroll = (from, to, echo, fromKey, toKey) => {
  const expected = echo[fromKey];
  echo[fromKey] = null;
  if (expected !== null && Math.abs(from.scrollLeft - expected) < 1) return;
  if (!to || Math.abs(to.scrollLeft - from.scrollLeft) < 1) return;
  echo[toKey] = from.scrollLeft;
  to.scrollLeft = from.scrollLeft;
};

const FloatingHorizontalScrollbar = ({ targetRef }) => {
  const barRef = useRef(null);
  const echoRef = useRef({ target: null, bar: null });
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      const next = measure(target);
      setMetrics((current) => (sameMetrics(current, next) ? current : next));
    };
    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const onTargetScroll = () => syncScroll(target, barRef.current, echoRef.current, "target", "bar");

    update();
    target.addEventListener("scroll", onTargetScroll, { passive: true });
    // En captura: el scroll vertical de la ventana o de cualquier ancestro desplazable mueve el contenedor.
    window.addEventListener("scroll", scheduleUpdate, { capture: true, passive: true });
    window.addEventListener("resize", scheduleUpdate);
    // Cambios de ancho del contenedor, de la tabla (filtros, columnas) o del alto de la página por encima de ella.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(scheduleUpdate) : null;
    if (observer) [target, ...target.children, document.body].forEach((element) => observer.observe(element));

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      target.removeEventListener("scroll", onTargetScroll);
      window.removeEventListener("scroll", scheduleUpdate, { capture: true });
      window.removeEventListener("resize", scheduleUpdate);
      if (observer) observer.disconnect();
    };
  }, [targetRef]);

  // Al aparecer (o cambiar el ancho del contenido) la barra arranca en la posición real del contenedor.
  useLayoutEffect(() => {
    const target = targetRef.current;
    const bar = barRef.current;
    if (!target || !bar || Math.abs(bar.scrollLeft - target.scrollLeft) < 1) return;
    echoRef.current.bar = target.scrollLeft;
    bar.scrollLeft = target.scrollLeft;
  }, [metrics, targetRef]);

  const handleBarScroll = useCallback(() => {
    if (barRef.current) syncScroll(barRef.current, targetRef.current, echoRef.current, "bar", "target");
  }, [targetRef]);

  if (!metrics) return null;
  return createPortal(
    <div
      ref={barRef}
      onScroll={handleBarScroll}
      aria-hidden="true"
      tabIndex={-1}
      data-testid="floating-horizontal-scrollbar"
      className="fixed bottom-0 z-30 overflow-x-auto overflow-y-hidden border-t border-slate-200 bg-white/95 shadow-[0_-2px_8px_rgba(15,23,42,0.08)]"
      style={{ left: metrics.left, width: metrics.width }}
    >
      <div style={{ width: metrics.scrollWidth, height: 1 }} />
    </div>,
    document.body
  );
};

export default FloatingHorizontalScrollbar;
